import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { ApiService, showToast } from './api.service';

interface InsuranceReport {
  id: number;
  report_no: string;
  status: string;
  reference_id?: string | null;
  model_type?: string;
  customer_name?: string;
  customer_email?: string;
  emailed_at?: string;
  site_address?: string;
  updated_at?: string;
  created_at?: string;
  item_count?: number;
}

@Component({
  selector: 'app-admin-insurance-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './admin-insurance-dashboard.component.html',
  styleUrl: './admin-insurance-dashboard.component.css'
})
export class AdminInsuranceDashboardComponent {
  private api = inject(ApiService);
  private http = inject(HttpClient);
  private router = inject(Router);

  reports = signal<InsuranceReport[]>([]);
  isLoading = signal(false);
  isCreating = signal(false);
  ownerName = signal('');
  address = signal('');
  customerEmail = signal('');

  ngOnInit(): void {
    this.loadReports();
  }

  loadReports(): void {
    this.isLoading.set(true);
    this.http.get<any>(`${this.api.baseUrl}/ai/list_insurance_reports.php`, { headers: (this.api as any).getHeaders() })
      .subscribe({
        next: (res) => {
          this.isLoading.set(false);
          if (res?.success) this.reports.set(res.data?.reports || []);
        },
        error: () => {
          this.isLoading.set(false);
          showToast('error', 'Could not load insurance reports');
        }
      });
  }

  createReport(): void {
    this.isCreating.set(true);
    this.http.post<any>(`${this.api.baseUrl}/ai/save_insurance_report.php`, {
      status: 'Draft',
      model_type: 'Manual',
      customer_name: this.ownerName() || 'Untitled owner',
      customer_email: this.customerEmail().trim(),
      site_address: this.address(),
      items: []
    }, { headers: (this.api as any).getHeaders() }).subscribe({
      next: (res) => {
        this.isCreating.set(false);
        if (res?.success && res.data?.id) {
          this.ownerName.set('');
          this.address.set('');
          this.customerEmail.set('');
          this.router.navigate(['/admin/ai/insurance', res.data.id]);
        } else {
          showToast('error', res?.message || 'Could not create report');
        }
      },
      error: (e) => {
        this.isCreating.set(false);
        showToast('error', e.error?.message || 'Could not create report');
      }
    });
  }

  openReport(report: InsuranceReport): void {
    this.router.navigate(['/admin/ai/insurance', report.id]);
  }

  deleteReport(report: InsuranceReport, event: Event): void {
    event.stopPropagation();
    if (!confirm(`Delete ${report.report_no}?`)) return;
    this.http.post<any>(`${this.api.baseUrl}/ai/delete_insurance_report.php`, { id: report.id }, { headers: (this.api as any).getHeaders() })
      .subscribe({
        next: (res) => {
          if (res?.success) {
            this.loadReports();
            showToast('success', 'Report deleted');
          } else {
            showToast('error', res?.message || 'Delete failed');
          }
        },
        error: () => showToast('error', 'Delete failed')
      });
  }

  attachedLabel(report: InsuranceReport): string {
    if (!report.reference_id || report.model_type === 'Manual') return 'Unattached';
    return `${report.model_type} - ${report.reference_id}`;
  }

  formatDate(value?: string): string {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-NZ', { month: 'short' });
    const year = String(date.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  }
}
